import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Divider,
} from '@mui/material';
import {
  HowToReg as ApprovalIcon,
  People as UserIcon,
  LocationOn as VenueIcon,
  Category as EventTypeIcon,
  TableRestaurant as TableReservationIcon,
  History as AuditLogIcon,
  CalendarMonth as WeeklyPlannerIcon,
  FitnessCenter as BalanceIcon,
  CreditCard as DebtIcon,
  DeleteSweep as DataRetentionIcon,
  SportsTennis as PrivateLessonRequestIcon,
} from '@mui/icons-material';
import UserApproval from './UserApproval';
import UserManagement from './UserManagement';
import VenueManagement from './VenueManagement';
import EventTypeManagement from './EventTypeManagement';
import PendingReservations from './PendingReservations/PendingReservations';
import PrivateLessonRequests from './PrivateLessonRequests/PrivateLessonRequests';
import { usePendingAdminCounts, PendingCounts, PendingKind } from '../hooks/usePendingAdminCounts';
import { labelWithPendingBadge } from './PendingCountBadge';
import AuditLogViewer from './AuditLog/AuditLogViewer';
import WeeklyPlanner from './WeeklyPlanner/WeeklyPlanner';
import AdminTrainingBalance from './AdminTrainingBalance';
import PrivateLessonDebts from './PrivateLessonDebts';
import DataRetention from './DataRetention';
import { DEFAULT_SETTINGS_SUB, SettingsSubKey } from './dashboardTabs';

interface SettingsManagementProps {
  onEditUser?: (userId: string) => void;
  /**
   * [Web UX 3. csomag (A), 2026-10-04] VEZÉRELT mód: az aktív al-fül KULCSA a Dashboardtól jön (az
   * URL `?al=` paraméteréből), és a váltást is a Dashboard végzi (`onSubTabChange`) — így az
   * al-fül is a mentetlen-kapun megy át, és `pushState`-tel az URL-be íródik (a Vissza gomb az
   * előző al-fülre visz). Mindkettő nélkül (önálló render, pl. `SettingsManagement.test.tsx`) a
   * komponens a régi módon, saját állapottal működik.
   */
  activeSubKey?: SettingsSubKey;
  onSubTabChange?: (key: SettingsSubKey) => void;
  /**
   * [Web UX 4. csomag (Admin teendők), 2026-10-04] A három függő szám a Dashboardtól (EGY közös forrás: `usePendingAdminCounts`),
   * és a gyermek-listák jelentése visszafelé (`onPendingCountChange`). `null` = még ismeretlen (nem 0). Mindkettő nélkül
   * (önálló render) a komponens maga hívja ugyanazt a hookot — Dashboard alatt viszont garantáltan csak egy forrás van.
   */
  pendingCounts?: PendingCounts;
  onPendingCountChange?: (kind: PendingKind, count: number) => void;
}

interface SettingsItem {
  /** Stabil kulcs — az URL-slug ehhez kötődik (`dashboardTabs.ts` `SETTINGS_SUB_SLUGS`), NEM az indexhez. */
  key: SettingsSubKey;
  label: string;
  icon: React.ReactElement;
  /** Jóváhagyásra váró darabszám jelvénye a címke után (0/ismeretlen → nincs). */
  badge?: { count: number | null; testId: string };
  component: React.ReactNode;
}

const SettingsManagement: React.FC<SettingsManagementProps> = ({
  onEditUser,
  activeSubKey,
  onSubTabChange,
  pendingCounts,
  onPendingCountChange,
}) => {
  const [ownSubKey, setOwnSubKey] = useState<SettingsSubKey>(DEFAULT_SETTINGS_SUB);
  const currentSubKey = activeSubKey ?? ownSubKey;
  const controlled = pendingCounts !== undefined && onPendingCountChange !== undefined;
  const own = usePendingAdminCounts(!controlled);
  const counts: PendingCounts = pendingCounts ?? own.counts;
  const reportCount = onPendingCountChange ?? own.report;
  // ⚠️ STABIL visszahívások (nem inline nyíl): a PrivateLessonRequests a saját useCallback/useEffect-jében függ az
  // onPendingCountChange identitásától — ha minden renderben új függvény menne, minden szülő-render újra lekérné a számot.
  const reportUsers = useCallback((n: number) => reportCount('users', n), [reportCount]);
  const reportReservations = useCallback((n: number) => reportCount('reservations', n), [reportCount]);
  const reportLessons = useCallback((n: number) => reportCount('lessons', n), [reportCount]);

  // Önálló módban (nincs Dashboard) a három szám mountkor ÉS minden al-fül-váltáskor frissül — a régi viselkedés (B2-1
  // validátor-lelet: a Badge a fül megnyitása nélkül is friss számot mutasson). Vezérelt módban ezt a Dashboard végzi, itt
  // NINCS saját lekérés (két forrás két számot adhatna).
  const refreshOwn = own.refresh;
  useEffect(() => {
    if (!controlled) refreshOwn();
  }, [currentSubKey, controlled, refreshOwn]);

  // [Web UX 8 (jelvény-javítás), 2026-10-09] A jelvény a címke UTÁN, a soron belül ül (`labelWithPendingBadge`), nem az
  // IKONON. Régen itt egy `withBadge(icon, …)` az ikon sarkára tette: élesben a „10" a címke első betűjére lógott, és a fül
  // teteje levágta (a Tab és a `.MuiTabs-scroller` is vág). Részletek és mérés: PendingCountBadge.tsx.

  const settingsItems: SettingsItem[] = [
    {
      key: 'approvals',
      label: 'Regisztrációk jóváhagyása',
      icon: <ApprovalIcon />,
      badge: { count: counts.users, testId: 'settings-subtab-badge-approvals' },
      component: <UserApproval onPendingCountChange={reportUsers} />,
    },
    {
      key: 'reservations',
      label: 'Asztalfoglalások jóváhagyása',
      icon: <TableReservationIcon />,
      badge: { count: counts.reservations, testId: 'settings-subtab-badge-reservations' },
      component: <PendingReservations onPendingCountChange={reportReservations} />,
    },
    {
      key: 'lessonRequests',
      label: 'Magánóra-igénylések',
      icon: <PrivateLessonRequestIcon />,
      badge: { count: counts.lessons, testId: 'settings-subtab-badge-lessonRequests' },
      component: <PrivateLessonRequests onPendingCountChange={reportLessons} />,
    },
    {
      key: 'weeklyPlanner',
      label: 'Heti Beosztás',
      icon: <WeeklyPlannerIcon />,
      component: <WeeklyPlanner />,
    },
    {
      key: 'balances',
      label: 'Játékosok Egyenlege',
      icon: <BalanceIcon />,
      component: <AdminTrainingBalance />,
    },
    {
      key: 'lessonDebts',
      // [Web UX 9 / T2, 2026-10-13] egységes „magánóra" írásmód (korábban „Magán óra tartozások", `SettingsManagement.tsx:135`)
      label: 'Magánóra-tartozások',
      icon: <DebtIcon />,
      component: <PrivateLessonDebts />,
    },
    {
      key: 'users',
      label: 'Felhasználó kezelés',
      icon: <UserIcon />,
      component: <UserManagement onEditUser={onEditUser} />,
    },
    {
      key: 'venues',
      label: 'Helyszín kezelés',
      icon: <VenueIcon />,
      component: <VenueManagement />,
    },
    {
      key: 'eventTypes',
      label: 'Esemény típus kezelés',
      icon: <EventTypeIcon />,
      component: <EventTypeManagement />,
    },
    {
      key: 'auditLog',
      label: 'Audit Napló',
      icon: <AuditLogIcon />,
      component: <AuditLogViewer />,
    },
    {
      key: 'dataRetention',
      label: 'Adattörlés',
      icon: <DataRetentionIcon />,
      component: <DataRetention />,
    },
  ];

  // Ismeretlen / hiányzó kulcs → az első al-fül (a régi `useState(0)` viselkedése).
  const activeIndex = Math.max(0, settingsItems.findIndex((item) => item.key === currentSubKey));

  const handleSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const key = settingsItems[newValue]?.key;
    if (!key) return;
    if (onSubTabChange) {
      // Vezérelt mód: a Dashboard dönt (mentetlen-kapu + URL), az állapot onnan jön vissza.
      onSubTabChange(key);
    } else {
      setOwnSubKey(key);
    }
  };

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom sx={{ mb: 3 }}>
        ⚙️ Beállítások
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeIndex}
          onChange={handleSubTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              minHeight: { xs: 48, sm: 64 },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
            },
          }}
        >
          {settingsItems.map((item) => (
            <Tab
              key={item.key}
              data-testid={`settings-subtab-${item.key}`}
              label={item.badge ? labelWithPendingBadge(item.label, item.badge.count, item.badge.testId) : item.label}
              icon={item.icon}
              iconPosition="start"
              sx={{
                // [Web UX 8 (jelvény-javítás), 2026-10-09] A MUI Tab alap `max-width: 360px`: a címke utáni jelvénnyel a
                // leghosszabb al-fül („Asztalfoglalások jóváhagyása" + „99+") e fölé nőne, és a fül `overflow: hidden`-je
                // a jelvényt vágná. A fülsor görgethető, így a korlát elhagyható.
                maxWidth: 'none',
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '1rem', sm: '1.2rem' },
                  mr: { xs: 0.5, sm: 1 },
                },
              }}
            />
          ))}
        </Tabs>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* Render active settings component */}
      {settingsItems[activeIndex]?.component}
    </Box>
  );
};

export default SettingsManagement;